/** Popover's default `marginThreshold`: how close to the viewport edge a menu may sit. */
const VIEWPORT_MARGIN_PX = 16;
export const V2_MENU_ANCHOR_ORIGIN = { vertical: 'bottom', horizontal: 'right' };
export const V2_MENU_TRANSFORM_ORIGIN = { vertical: 'top', horizontal: 'right' };
const paperObservers = new WeakMap();
/**
 * Popover never flips: when a menu below its trigger runs past the bottom of the
 * viewport, it slides the menu up over the trigger. These handlers watch where
 * Popover puts the paper (on open and on every reposition, e.g. resize) and, when
 * that happened and the menu fits above, shift it there with `translate`, a
 * property Popover never writes, so its own `top` stays the source of truth.
 */
export function getFlipAboveTransitionHandlers(ownerState) {
    return {
        onEntering: (paper) => {
            const anchor = resolveAnchor(ownerState.anchorEl);
            if (!anchor || !opensBelowAnchor(ownerState)) {
                return;
            }
            let placedTop = '';
            const update = () => {
                if (paper.style.top === placedTop) {
                    return;
                }
                placedTop = paper.style.top;
                placeAboveWhenClamped(paper, anchor);
            };
            stopWatching(paper);
            const observer = new MutationObserver(update);
            observer.observe(paper, { attributes: true, attributeFilter: ['style'] });
            paperObservers.set(paper, observer);
            // Popover positions right after this handler; a kept-mounted menu may get the
            // same `top` again, which changes no attribute and so notifies no observer.
            queueMicrotask(update);
        },
        onExited: stopWatching,
    };
}
// Unset origins fall back to Menu's own bottom/top pair, so only an explicit
// side or upward placement (submenus, context menus) counts as not below.
export function opensBelowAnchor({ anchorReference, anchorOrigin, transformOrigin, }) {
    return (anchorReference !== 'anchorPosition' &&
        (anchorOrigin?.vertical ?? 'bottom') === 'bottom' &&
        (transformOrigin?.vertical ?? 'top') === 'top');
}
function placeAboveWhenClamped(paper, anchor) {
    const top = Number.parseFloat(paper.style.top);
    if (Number.isNaN(top)) {
        return;
    }
    const gap = Number.parseFloat(getComputedStyle(paper).marginTop) || 0;
    const anchorRect = anchor.getBoundingClientRect();
    const shownTop = top + gap;
    const aboveTop = anchorRect.top - gap - paper.offsetHeight;
    const coversAnchor = shownTop < anchorRect.bottom;
    if (!coversAnchor || aboveTop < VIEWPORT_MARGIN_PX) {
        paper.style.translate = '';
        return;
    }
    paper.style.translate = `0 ${Math.round(aboveTop - shownTop)}px`;
    const [originX] = paper.style.transformOrigin.split(' ');
    paper.style.transformOrigin = `${originX} ${paper.offsetHeight}px`;
}
function stopWatching(paper) {
    paperObservers.get(paper)?.disconnect();
    paperObservers.delete(paper);
    paper.style.translate = '';
}
function resolveAnchor(anchorEl) {
    const anchor = typeof anchorEl === 'function' ? anchorEl() : anchorEl;
    return anchor ?? null;
}
